import React from "react";
import type { DocumentData } from "firebase/firestore";
import { Section } from "./ui/Section";
import { DetailList, DetailRow } from "./ui/Cards";
import { formatPhone } from "./ui/PhoneInput";
import {
  USER_CATEGORY_LABELS,
  UserCategory,
} from "./utils/getUserCategory";

/**
 * ProfileCard – karta „Moje údaje" (Section + DetailRow). Nahrazuje ručně
 * psanou kartu z UserProfilePage (a její kopii v UpdateProfile); zobrazuje
 * stejná data jako dřív – jméno, e-mail, číslo uživatele, telefon, číslo
 * a platnost ZP, kategorii – a navíc ročník a státní občanství, ze kterých
 * se kategorie odvozuje.
 *
 * Čistě prezentační: nic nenačítá (dokument dodá useCurrentUser, kategorii
 * useUserCategory). Bez dokumentu nevykreslí nic – stav načítání řeší
 * obrazovka (AsyncState).
 */

export interface ProfileCardProps {
  /** Data dokumentu users/{uid} (useCurrentUser().userDoc). */
  userDoc: DocumentData | null | undefined;
  /** E-mail z Firebase Auth – má přednost před userDoc.email (tak to profil ukazoval). */
  email?: string | null;
  /**
   * Kategorie z useUserCategory: hodnota → popisek, null → „Načítám…",
   * undefined (prop vynechán) → řádek Kategorie se nezobrazí.
   */
  category?: UserCategory | null;
  /** Titulek sekce; výchozí "Moje údaje". */
  title?: string;
  /** Akce v hlavičce sekce (např. tlačítko Upravit). */
  actions?: React.ReactNode;
  className?: string;
}

const notSet = <span className="lks-detailrow__empty">Neuvedeno</span>;

/** Textová hodnota pole, nebo null pro prázdné / nepodporované typy. */
const text = (raw: unknown): string | null => {
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  if (typeof raw === "string" && raw.trim() !== "") return raw.trim();
  return null;
};

/** První písmeno velké ("česká" → "Česká"). */
const capitalize = (s: string): string =>
  s.charAt(0).toLocaleUpperCase("cs-CZ") + s.slice(1);

/**
 * Datum z date inputu ("YYYY-MM-DD"), Firestore Timestamp nebo Date → Date.
 * "YYYY-MM-DD" se skládá v místním čase (new Date("YYYY-MM-DD") je UTC
 * půlnoc a v západních pásmech by ukázal předchozí den).
 */
const toDate = (raw: unknown): Date | null => {
  if (!raw) return null;
  if (typeof raw === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    // Importované záznamy mívají „31. 5. 2027" – new Date() by to četl
    // americky (měsíc. den.) a u dnů ≤ 12 prohodil den s měsícem.
    const cz = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(raw.trim());
    if (cz) return new Date(Number(cz[3]), Number(cz[2]) - 1, Number(cz[1]));
  }
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (
    typeof raw === "object" &&
    typeof (raw as { toDate?: unknown }).toDate === "function"
  ) {
    const d = (raw as { toDate: () => Date }).toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (typeof raw === "string" || typeof raw === "number") {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** "31. 5. 2027" – český zápis bez úvodních nul. */
const formatDate = (d: Date): string =>
  `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;

/** Platnost ZP: datum + upozornění, je-li prošlá. */
const renderValidity = (raw: unknown): React.ReactNode => {
  const date = toDate(raw);
  if (!date) {
    const fallback = text(raw);
    return fallback ?? notSet;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expired = date.getTime() < today.getTime();
  return (
    <>
      {formatDate(date)}
      {expired ? (
        <span className="lks-detailrow__note">Platnost vypršela</span>
      ) : null}
    </>
  );
};

export /** Uložené hodnoty („česká"/„slovenská") jsou klíč pro vlajky a filtry – mění se jen popisek. */
const NATIONALITY_LABELS: Record<string, string> = {
  "česká": "Česká republika",
  "slovenská": "Slovenská republika",
};
const nationalityLabel = (value: string): string =>
  NATIONALITY_LABELS[value.trim().toLowerCase()] ?? capitalize(value);

const ProfileCard: React.FC<ProfileCardProps> = ({
  userDoc,
  email,
  category,
  title = "Moje údaje",
  actions,
  className,
}) => {
  if (!userDoc) return null;

  const name = [text(userDoc.firstName), text(userDoc.lastName)]
    .filter(Boolean)
    .join(" ");
  const mail = text(email) ?? text(userDoc.email);
  const phone = formatPhone(userDoc.phone);
  const nationality = text(userDoc.nationality);

  return (
    <Section
      title={title}
      icon="bi-person-vcard"
      actions={actions}
      className={className}
    >
      <DetailList>
        <DetailRow label="Jméno" value={name || notSet} />
        <DetailRow label="E-mail" value={mail ?? notSet} />
        <DetailRow label="Telefon" value={phone || notSet} />
        <DetailRow
          label="Číslo uživatele"
          value={text(userDoc.sequenceNumber) ?? notSet}
        />
        {category !== undefined ? (
          <DetailRow
            label="Kategorie"
            value={
              category === null ? (
                <span className="lks-detailrow__empty">Načítám…</span>
              ) : (
                USER_CATEGORY_LABELS[category]
              )
            }
          />
        ) : null}
        <DetailRow label="Ročník" value={text(userDoc.birthYear) ?? notSet} />
        <DetailRow
          label="Státní občanství"
          value={nationality ? nationalityLabel(nationality) : notSet}
        />
        <DetailRow label="Číslo ZP" value={text(userDoc.zpNumber) ?? notSet} />
        <DetailRow
          label="Platnost ZP"
          value={renderValidity(userDoc.zpValidity)}
        />
      </DetailList>
    </Section>
  );
};

export default ProfileCard;
